import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../../components/LegalPage';
import legalConfig from '../../lib/legalConfig';
import { useLanguage } from '../../context/LanguageContext';

const META_DESC =
  'CosmoEstates Acceptable Use & Listing Policy: the rules for posting property listings, prohibited content, moderation, and consequences for breach.';

export default function AcceptableUsePage() {
  const { t } = useLanguage();
  return (
    <LegalPage
      title={t('legal.docs.acceptableUseTitle')}
      effectiveDate={legalConfig.effectiveDate}
      metaTitle="Acceptable Use & Listing Policy | CosmoEstates"
      metaDescription={META_DESC}
    >
      <div className="legal-meta">
        <p>
          <strong>CosmoEstates</strong> — {legalConfig.entityName}
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
        </p>
      </div>

      <p className="legal-draft-note">
        Draft for review by Cyprus-qualified counsel. Not legal advice. This policy forms part
        of the <Link to="/terms">Terms &amp; Conditions</Link>.
      </p>

      <h2>1. Purpose</h2>
      <p>
        This policy sets the rules for using CosmoEstates and for posting listings. It keeps the
        marketplace accurate, lawful, and trustworthy. By using the Platform you agree to follow
        it.
      </p>

      <h2>2. General acceptable use</h2>
      <p>You must not:</p>
      <ul>
        <li>use the Platform for any unlawful, fraudulent, or harmful purpose;</li>
        <li>
          post content that is false, misleading, defamatory, obscene, discriminatory, or that
          infringes anyone's rights (including intellectual property and privacy rights);
        </li>
        <li>
          impersonate any person or misrepresent your identity, affiliation, or status (e.g.
          posing as an agent or owner you are not);
        </li>
        <li>harass, abuse, or harm other users;</li>
        <li>
          upload viruses or malicious code, or attempt to disrupt, overload, or gain
          unauthorised access to the Platform or its systems;
        </li>
        <li>
          scrape, crawl, harvest, or bulk-extract data or listings except as expressly permitted
          in writing;
        </li>
        <li>circumvent posting limits, fees, verification, or security measures; or</li>
        <li>
          misuse personal data obtained through the Platform (for example, contact details from
          leads) for anything other than the permitted purpose, in breach of data protection
          law.
        </li>
      </ul>

      <h2>3. Listing rules</h2>
      <p>When you list a property you must:</p>
      <ul>
        <li>
          have the legal right to advertise it (as owner, or with the owner's authorisation);
        </li>
        <li>
          provide <strong>accurate, current, and complete</strong> information — correct
          location, type, size, price, and condition;
        </li>
        <li>
          use only images you have the right to use, that genuinely depict the property;
        </li>
        <li>
          list each property <strong>once</strong> — no duplicate listings of the same property;
        </li>
        <li>set a genuine asking price or rent — no bait pricing or hidden terms; and</li>
        <li>keep the listing up to date and remove or mark it when no longer available.</li>
      </ul>

      <h2>4. Prohibited listings and content</h2>
      <p>The following are not allowed:</p>
      <ul>
        <li>
          duplicate, fake, expired, or "phantom" listings (properties that are not genuinely
          available);
        </li>
        <li>
          scams, including requests for upfront payments/deposits to "hold" a property,
          wire-transfer fraud, and phishing;
        </li>
        <li>
          listings that are misleading about price, availability, ownership, or features;
        </li>
        <li>
          properties that cannot lawfully be sold or let, or content that breaches Cyprus law;
        </li>
        <li>
          discriminatory wording (e.g. excluding tenants/buyers on the basis of a protected
          characteristic) contrary to law;
        </li>
        <li>off-platform schemes designed to avoid fees or our protections; and</li>
        <li>spam, repetitive postings, or advertising unrelated products/services.</li>
      </ul>

      <h2>5. Posting limits</h2>
      <p>
        We may limit how many listings you can post in a period. Limits, where they apply, are
        shown on the Platform and may change with notice. We may apply different limits to
        private users, independent agents, and partner agents.
      </p>

      <h2>6. Moderation, checks, and takedown</h2>
      <p>
        6.1 We may review listings and content for duplication, quality, scams, and suspicious
        activity, and we may{' '}
        <strong>refuse, edit, hold, downrank, or remove</strong> any listing or content at our
        discretion, with or without notice.
      </p>
      <p>
        6.2 We may verify ownership, identity, or professional licensing before or after
        publication.
      </p>
      <p>
        6.3 We are not obliged to monitor all content, and any checks we do are not a guarantee
        that a listing is accurate or legitimate.
      </p>
      <p>
        6.4 <strong>Reporting.</strong> If you see a listing or message that looks fraudulent,
        duplicated, or otherwise breaches this policy, report it to {legalConfig.contactEmail}.
        We will review reports and act where appropriate. As an information-society service
        under the Cyprus Electronic Commerce Law 156(I)/2004, we will act to remove or disable
        access to unlawful content once we obtain actual knowledge of it.
      </p>

      <h2>7. Featured / Boosted listings</h2>
      <p>
        Boosted or featured placement increases visibility but does <strong>not</strong> mean we
        endorse, verify, or guarantee a listing. Boosted listings must still comply with this
        policy. See the <Link to="/billing">Subscription, Boost &amp; Refund Terms</Link>.
      </p>

      <h2>8. Professional users</h2>
      <p>
        Agents, partners, and contractors must additionally: hold all required Cyprus
        registrations, licences, and insurance; deal honestly with users and leads; and comply
        with consumer protection and data protection law when contacting and handling leads
        obtained through the Platform.
      </p>

      <h2>9. Consequences of breach</h2>
      <p>
        If you breach this policy we may remove content, restrict features, suspend or terminate
        your account, withhold or refuse refunds where permitted, retain evidence, and report
        unlawful activity to the authorities. Serious or repeated breaches may lead to a
        permanent ban.
      </p>

      <h2>10. Contact</h2>
      <p>Questions or reports: {legalConfig.contactEmail}.</p>
    </LegalPage>
  );
}
